import React, { useCallback, useEffect, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import type { AxiosInstance } from "axios";
import {
  addTicketParticipants,
  getNewTicketCandidateTenants,
  getNewTicketParticipantCandidates,
  type ParticipantCandidate,
  type ParticipantCandidateTenant,
} from "../../request/request";
import { ParticipantPicker } from "../ParticipantPicker";

/**
 * Participants picked while a Kontrol Edge ticket is being created. They
 * belong to the end customer chosen in the form, so changing it drops them:
 * the people allowed on a ticket for Acme are not those allowed for Beta.
 */
export function useNewTicketParticipants(endCustomerId: number | undefined) {
  const [state, setState] = useState<{
    endCustomerId: number | undefined;
    selected: ParticipantCandidate[];
  }>({ endCustomerId, selected: [] });

  const selected =
    state.endCustomerId === endCustomerId ? state.selected : [];
  const setSelected = useCallback(
    (next: ParticipantCandidate[]) =>
      setState({ endCustomerId, selected: next }),
    [endCustomerId]
  );
  const resetParticipants = useCallback(
    () => setState({ endCustomerId: undefined, selected: [] }),
    []
  );

  return { participants: selected, setParticipants: setSelected, resetParticipants };
}

/**
 * What to run once the ticket exists: add the picked people to it, through
 * the same endpoint as the ticket detail, which also creates their support
 * portal accounts. Nothing to run when nobody was picked.
 */
export function addParticipantsOnceCreated(
  api: AxiosInstance,
  participants: ParticipantCandidate[]
) {
  if (participants.length === 0) return undefined;
  const emails = participants.map((p) => p.email);
  return (ticketKey: string) => addTicketParticipants(api, ticketKey, emails);
}

interface NewTicketParticipantsProps {
  endCustomerId: number | undefined;
  selected: ParticipantCandidate[];
  onChange: (selected: ParticipantCandidate[]) => void;
}

/**
 * "Who else should follow this ticket" in the last step of the Kontrol Edge
 * create modals (CTX-1256). The customers on offer come from the backend
 * (CTX-1258) and match what the ticket detail offers once the ticket exists.
 * Renders nothing when there is nobody the user may add.
 */
export function NewTicketParticipants({
  endCustomerId,
  selected,
  onChange,
}: NewTicketParticipantsProps) {
  const api = useAxiosAuth0();
  const [candidateTenants, setCandidateTenants] = useState<
    ParticipantCandidateTenant[]
  >([]);

  useEffect(() => {
    setCandidateTenants([]);
    if (endCustomerId == null) return;
    let cancelled = false;
    getNewTicketCandidateTenants(api, endCustomerId)
      .then((tenants) => {
        if (!cancelled) setCandidateTenants(tenants);
      })
      // Participants are optional: without candidates the ticket can still
      // be created, so the section just stays hidden.
      .catch((err) => console.error("Error loading participant candidates =>", err));
    return () => {
      cancelled = true;
    };
  }, [api, endCustomerId]);

  const loadCandidates = useCallback(
    (tenantId: number) =>
      endCustomerId == null
        ? Promise.resolve([])
        : getNewTicketParticipantCandidates(api, endCustomerId, tenantId),
    [api, endCustomerId]
  );

  if (candidateTenants.length === 0) return null;

  return (
    <section className="rounded-md border border-semantic-border-primary p-4">
      <p className="text-sm font-medium text-semantic-text-primary">
        People to follow this ticket
      </p>
      <p className="mb-3 text-xs text-semantic-text-secondary">
        Optional. They will see it in their own list and get its replies and
        status changes by email; the rest of their organization will not.
      </p>
      <ParticipantPicker
        candidateTenants={candidateTenants}
        loadCandidates={loadCandidates}
        selected={selected}
        onChange={onChange}
        emptyItemsMessage="Nobody else in this customer to add"
      />
    </section>
  );
}

export default NewTicketParticipants;
